/**
 * Shared formatting utilities for consistent display across components
 */

/**
 * Format currency values with consistent precision for billing display
 * Uses 4 decimal places to match billing system precision
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4
  }).format(amount);
}

/**
 * Format timestamp for consistent display across components
 * Resilient to various input types and invalid dates
 */
export function formatTimestamp(input: unknown): string {
  try {
    const d = typeof input === 'string' || typeof input === 'number'
      ? new Date(input)
      : input instanceof Date
        ? input
        : new Date();
    return isNaN(d.getTime()) ? 'invalid' : d.toLocaleString();
  } catch {
    return 'invalid';
  }
}

/**
 * Format duration in milliseconds to human readable format
 */
export function formatDuration(durationMs: number): string {
  if (durationMs < 1000) {
    return `${durationMs}ms`;
  }

  const seconds = Math.round(durationMs / 1000);
  if (seconds < 60) {
    return `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s`;
}

/**
 * Format large numbers with appropriate units (K, M, B)
 */
export function formatNumber(value: number): string {
  if (value >= 1_000_000_000) {
    return `${(value / 1_000_000_000).toFixed(1)}B`;
  }
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }
  return value.toString();
}